import { notifications } from '@mantine/notifications';

interface ToastOptions {
  id?: string;
  duration?: number;
}

let counter = 0;

const show = (
  color: string | undefined,
  message: string,
  options: ToastOptions = {},
  loading = false,
) => {
  const id = options.id || `toast-${Date.now()}-${counter++}`;
  notifications.show({
    id,
    color,
    loading,
    message,
    autoClose: loading ? false : (options.duration ?? 4000),
    withCloseButton: !loading,
  });
  return id;
};

/**
 * Notifications with the call shape the pages already use (the same as
 * react-hot-toast): `toast.success(text)`, `toast.error(text)`,
 * `const id = toast.loading(text)`, `toast.dismiss(id)`.
 */
const toast = Object.assign(
  (message: string, options?: ToastOptions) => show(undefined, message, options),
  {
    success: (message: string, options?: ToastOptions) =>
      show('green', message, options),
    error: (message: string, options?: ToastOptions) =>
      show('red', message, options),
    loading: (message: string, options?: ToastOptions) =>
      show(undefined, message, options, true),
    dismiss: (id?: string) =>
      id ? notifications.hide(id) : notifications.clean(),
    /** Loading notification that turns into the outcome of the promise. */
    promise: <T,>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string | ((result: T) => string);
        error: string | ((error: any) => string);
      },
    ): Promise<T> => {
      const id = show(undefined, messages.loading, {}, true);
      const settle = (color: string, message: string) =>
        notifications.update({
          id,
          color,
          message,
          loading: false,
          autoClose: 4000,
          withCloseButton: true,
        });

      promise.then(
        (result) =>
          settle(
            'green',
            typeof messages.success === 'function'
              ? messages.success(result)
              : messages.success,
          ),
        (error) =>
          settle(
            'red',
            typeof messages.error === 'function'
              ? messages.error(error)
              : messages.error,
          ),
      );
      return promise;
    },
  },
);

export { toast };
export default toast;
